import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPerspectiveScene, DEFAULT_PARAMS } from "./scene";
import PerspectivePanel from "./PerspectivePanel";

/**
 * Laboratory 2 · Perspective
 *
 * Route: /lab/perspective
 * Governed by: /app/memory/SPATIAL_SEMANTICS.md
 *
 * Not SONMI. Not the presentation. A research environment for one
 * question: can relationships emerge solely through changes in
 * perspective?
 */

export default function PerspectiveLab() {
  const containerRef = useRef(null);
  const sceneApiRef = useRef(null);

  const [params, setParams] = useState({ ...DEFAULT_PARAMS });
  const [uiHidden, setUiHidden] = useState(false);
  const [readouts, setReadouts] = useState({});

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const api = createPerspectiveScene({
      container,
      initialParams: params,
    });
    sceneApiRef.current = api;

    api.setOnFrame((frame) => {
      setReadouts(frame);
    });

    return () => {
      api.dispose();
      sceneApiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sceneApiRef.current) {
      sceneApiRef.current.setParams(params);
    }
  }, [params]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if (e.key === "h" || e.key === "H") setUiHidden((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onParamChange = useCallback((key, value) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetScene = useCallback(() => {
    setParams({ ...DEFAULT_PARAMS });
  }, []);

  const takeScreenshot = useCallback(() => {
    if (sceneApiRef.current) {
      sceneApiRef.current.screenshot({ presetName: "perspective" });
    }
  }, []);

  return (
    <div
      data-testid="perspective-lab"
      style={{
        position: "fixed",
        inset: 0,
        background: "#000",
        overflow: "hidden",
      }}
    >
      <div
        ref={containerRef}
        data-testid="perspective-lab-canvas"
        style={{ position: "absolute", inset: 0 }}
      />

      {uiHidden && (
        <button
          onClick={() => setUiHidden(false)}
          data-testid="btn-show-ui"
          style={{
            position: "fixed",
            top: 16,
            right: 16,
            background: "rgba(0,0,0,0.35)",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "rgba(255,255,255,0.5)",
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            fontSize: 10,
            padding: "4px 10px",
            borderRadius: 3,
            cursor: "pointer",
            letterSpacing: "0.12em",
            zIndex: 10,
          }}
        >
          show (H)
        </button>
      )}

      {!uiHidden && (
        <PerspectivePanel
          params={params}
          onParamChange={onParamChange}
          onReset={resetScene}
          onScreenshot={takeScreenshot}
          onHideUI={() => setUiHidden(true)}
          readouts={readouts}
        />
      )}
    </div>
  );
}
